import React, { useState, useEffect } from 'react'
import { FragmentType, graphql, useFragment } from '../../../gql'
import { Button } from '../../../components/shadcn/Button'
import Modal from '../../../components/Modal'
import { ControlledInput } from '../../../components/Form/Controlled/ControlledInput'
import { ControlledSelect } from '../../../components/Form/Controlled/ControlledSelect'
import { ControlledTextarea } from '../../../components/Form/Controlled/ControlledTextarea'
import { useForm } from 'react-hook-form'
import { faUndo } from '@fortawesome/pro-solid-svg-icons'
import { useBarcode, useBarcodePosition } from '../../../hooks/useBarcode'
import { useOrderDetailContext } from '../OrderDetailContext'
import { useBasicEntities } from '../../../hooks/useBasicEntities'

export const EditReturnItemModal_OrderItemFragment = graphql(`
  fragment EditReturnItemModal_OrderItemFragment on OrderItem {
    id
    name
    code
    ean
    quantity
    note
    product {
      id
      name
      isBundle
    }
    moves {
      id
      amount
      productDepotFrom {
        id
        batch
        expirationDate
        position1
        position2
        position3
        depot {
          id
          name
        }
        supplier {
          id
          name
        }
      }
    }
  }
`)

export const EditReturnItemModal_OrderFragment = graphql(`
  fragment EditReturnItemModal_OrderFragment on Order {
    id
    company {
      id
    }
  }
`)

interface EditReturnItemModalProps {
  orderItem: FragmentType<typeof EditReturnItemModal_OrderItemFragment>
  orderId: number
  defaultDepotId?: number
  quantityReturned?: number
  onConfirm: (data: ReturnFormData) => void
  disabled?: boolean
  depots?: { id: number; name: string }[]
  onClose?: () => void
  isOpen?: boolean
}

export interface ReturnFormData {
  orderItemId: number
  productId: number
  amount: number
  depotId: number
  batch?: string
  expirationDate?: string
  position1?: string
  position2?: string
  position3?: string
  note?: string
  serialNumbers?: string[]
}

export const EditReturnItemModal: React.FC<EditReturnItemModalProps> = ({
  orderItem: orderItemProp,
  orderId,
  defaultDepotId,
  quantityReturned = 0,
  onConfirm,
  disabled,
  onClose,
  isOpen: controlledIsOpen,
}) => {
  const orderItem = useFragment(EditReturnItemModal_OrderItemFragment, orderItemProp)
  const context = useOrderDetailContext()
  const order = useFragment(EditReturnItemModal_OrderFragment, context.order)
  const { depots: allDepots } = useBasicEntities()
  const [internalIsOpen, setInternalIsOpen] = useState(false)
  const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen

  // Use depots from useBasicEntities filtered by company
  const depots = allDepots.filter(d => d.company.id === order.company.id)

  const firstMove = orderItem.moves?.[0]
  const defaultValues = {
    amount: orderItem.quantity - quantityReturned,
    depotId:
      (defaultDepotId ? defaultDepotId.toString() : null) ||
      firstMove?.productDepotFrom?.depot?.id?.toString() ||
      depots[0]?.id?.toString() ||
      '',
    batch: firstMove?.productDepotFrom?.batch || '',
    expirationDate: firstMove?.productDepotFrom?.expirationDate || '',
    position1: firstMove?.productDepotFrom?.position1 || '',
    position2: firstMove?.productDepotFrom?.position2 || '',
    position3: firstMove?.productDepotFrom?.position3 || '',
    note: orderItem.note || '',
  }

  const { control, handleSubmit, reset, setValue, getValues } = useForm({ defaultValues })

  const handleOpen = () => {
    reset(defaultValues)
    if (controlledIsOpen === undefined) setInternalIsOpen(true)
  }

  const handleClose = () => {
    if (onClose) onClose()
    if (controlledIsOpen === undefined) setInternalIsOpen(false)
  }

  const handleConfirm = handleSubmit(data => {
    onConfirm({
      orderItemId: orderItem.id,
      productId: orderItem.product?.id || 0,
      ...data,
      depotId: typeof data.depotId === 'string' ? parseInt(data.depotId) : data.depotId,
    })
    handleClose()
  })

  // Handle position scanning
  useBarcodePosition(
    position => {
      setValue('position1', position.position1)
      setValue('position2', position.position2)
      setValue('position3', position.position3)
    },
    { autoStart: true },
  )

  const depotOptions = depots.map(d => ({ label: d.name, value: d.id.toString() }))

  return (
    <>
      <Button
        variant="default"
        size="md"
        icon={faUndo}
        onClick={handleOpen}
        disabled={disabled || orderItem.product?.isBundle}>
        Vrátit
      </Button>

      <Modal isOpen={isOpen} onClose={handleClose} title={`Vyplnit vratku: ${orderItem.name}`}>
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <ControlledInput
              control={control}
              name="amount"
              type="number"
              label="Množství"
              min={0}
              max={orderItem.quantity - quantityReturned}
            />
            <ControlledInput control={control} name="expirationDate" type="date" label="Expirace" />
            <ControlledInput control={control} name="batch" type="text" label="Šarže" />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <ControlledInput control={control} name="position1" type="text" label="Pozice 1" />
            <ControlledInput control={control} name="position2" type="text" label="Pozice 2" />
            <ControlledInput control={control} name="position3" type="text" label="Pozice 3" />
          </div>

          <div>
            <ControlledSelect
              control={control}
              name="depotId"
              label="Sklad"
              options={depotOptions}
              className="w-full"
            />
          </div>

          <div>
            <ControlledTextarea control={control} name="note" label="Poznámka" />
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <Button variant="outline" onClick={handleClose}>
            Zrušit
          </Button>
          <Button variant="default" onClick={handleConfirm}>
            Potvrdit
          </Button>
        </div>
      </Modal>
    </>
  )
}
